import { Control, useFieldArray, useFormContext } from 'react-hook-form';
import { YesNoStyledRadioGroup } from '../styled/StyledRadioGroup';
import StyledFormSection from '../styled/StyledFormSection';
import InsuranceCard from './InsuranceCard';
import PlusIcon from '@/assets/icons/plus.svg?react';
import StyledButton from '../styled/StyledButton';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';
import TranslateIcon from '../translation/TranslateIcon';
import { useTranslateFields } from '@/hooks/useTranslate';

interface InsuranceListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const InsuranceList = ({
  control,
  language,
  isViewMode = false,
}: InsuranceListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'insurances',
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: 'insurances',
    });

  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const { translateFields, isTranslating } = useTranslateFields({
    control: control,
    setValue,
    fields: [],
    nestedFields: {
      insurances: ['name', 'shortDescription', 'longDescription'],
    },
    targetLanguage: language,
  });

  return (
    <StyledFormSection
      sectionTitle={t('carRental.categoryForm.addInsurance')}
      icon={
        <TranslateIcon
          translateFields={() => translateFields({ allNestedFields: true })}
          isTranslating={isTranslating}
          language={language}
          hidden={isViewMode}
        />
      }
    >
      <div className="flex flex-col gap-1.5">
        <p className="text-sm leading-6 font-medium text-gray-700">
          {t('carRental.categoryForm.limitInsuranceQuestion')}
        </p>
        <YesNoStyledRadioGroup
          control={control}
          name={'shouldLimitInsurance'}
          disabled={isViewMode}
        />
      </div>
      {fields.map((item, index) => (
        <InsuranceCard
          key={item.id}
          control={control}
          index={index}
          language={language}
          deleteInsurance={() => {
            remove(index);
            removeTranslation(index);
          }}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addInsurance')}
          onClick={() => {
            append({
              name: undefined,
              shortDescription: undefined,
              longDescription: undefined,
              amount: undefined,
              billingType: undefined,
            } as CarRentalFormDataWithTranslations['insurances'][number]);
            appendTranslation(() => ({
              name: undefined,
              shortDescription: undefined,
              longDescription: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </StyledFormSection>
  );
};

export default InsuranceList;
